/**
 * API key for `api.mysticfinance.xyz`, set by the host process.
 *
 * The `morphoCache` endpoint used to be open; as of 2026-09 it answers every
 * request — including one with no `chainId` — with
 * `401 {"message":"API key required. Send it as the \`x-api-key\` header."}`.
 *
 * **Nothing sets this today and no key exists in either repo**, so the Mystic
 * remote is inert in production; `mysticPricesAvailable` therefore routes the
 * Mystic chains (Flare 14, Plume 98866, Citrea 4114) to the ON-CHAIN Morpho
 * oracle multicall, which is their source of record. That path is complete on
 * its own — verified 2026-09-07 with no key: 9/9 published Flare markets and
 * 9/11 Plume markets priced, all carrying the real oracle ratio Mystic never
 * returns. Two things must hold for it to stay that way, and both are
 * off-by-default failure modes rather than errors:
 *
 *  1. `lender-metadata` must keep DISCOVERING markets on these chains, and
 *     today it CANNOT. Its `update:onchain-markets` event scan no longer
 *     credits `hasMysticApi()` (fixed 2026-09-07 — it had been excluding these
 *     chains on the strength of an indexer that answers 401), but the scan
 *     itself does not run here either: Plume's drpc endpoint refuses
 *     `eth_getLogs` outright, Citrea serves no usable range, and Flare caps a
 *     range at 30 blocks over a 69M-block history so the budgeted scan returns
 *     ZERO markets while reporting success. The roster is therefore frozen at
 *     what Mystic last served (9 Flare / 14 Plume / 1 Citrea) and a market
 *     created after that is invisible. Discovery needs a key or a paid
 *     archival RPC; it is append-only, so nothing is ever deleted by this.
 *  2. Some other lender on the chain must publish a USD price for the loan
 *     asset, since Morpho entries are derived (Kinetic/Enosys on Flare,
 *     Avalon/LayerBank on Plume). Citrea has no other lender at all.
 *
 * A setter exists so worker/browser hosts — which have no `process.env` — can
 * supply the key, matching `setResupplyPairRoster` / `setCurvanceAssetRoster`.
 * When nothing has been set, `MYSTIC_API_KEY` is read from the environment so
 * Node hosts (the yield-tracer crons) need no wiring of their own.
 */

let mysticApiKey: string | undefined

/** Publish the key. Pass undefined/empty to fall back to `MYSTIC_API_KEY`. */
export function setMysticApiKey(key: string | undefined): void {
  mysticApiKey = key && key.length > 0 ? key : undefined
}

export function getMysticApiKey(): string | undefined {
  if (mysticApiKey) return mysticApiKey
  // Guarded: this module is bundled for the browser too, where `process` is
  // undefined and touching it throws rather than returning undefined.
  const fromEnv =
    typeof process !== 'undefined' ? process.env?.MYSTIC_API_KEY : undefined
  return fromEnv && fromEnv.length > 0 ? fromEnv : undefined
}
